import type { ButtonProps } from '@mui/material';

import type { AnimationStyleEnum } from './types';

/*
NOTE:
All new feature flags should go to LaunchDarkly

When adding feature flags to LaunchDarkly,
remember to enable "Client-Side SDK Availability"
*/

export enum FeatureFlagName {
  OpenaiDefaultModel = 'openaiDefaultModel',
  CloneProject = 'cloneProject',
  AnimationStyleSelectionVideos = 'animationStyleSelectionVideos',
  PostProductionMediaManagement = 'postProductionMediaManagement',
  RoleCastingTalentAgeFilter = 'roleCastingTalentAgeFilter',
  TalentBadMatchThreshold = 'talentBadMatchThreshold',
  GoogleDocsCallSheetExperiment = 'googleDocsCallSheetExperiment', // after project finalization use project.wasFinalizedWithGoogleDocCallSheetExperiment
  CallSheetV2 = 'callSheetV2',
  TestProjectButtonColor = 'testProjectButtonColor',
  HackathonCallSheetPoc = 'hackathonCallSheetPoc',
  Tasks = 'tasks', // enables the creation of tasks at project creation
  TimelineV2 = 'timelineV2',
  AdminReports = 'adminReports',
  GenerateContractFromSources = 'generateContractFromSources',
  ScriptedCallSheet = 'callSheetScripted',
  // Proposals
  CreativeProposals = 'creativeProposals',
  ProposalProcessDefaultImages = 'proposalProcessDefaultImages',
  ProposalDefaultImages = 'proposalDefaultImages',
  CreativeProposalBudget = 'creativeProposalBudget',
  StandaloneScriptPage = 'standaloneScriptPage',
  GeminiStoryboardGeneration = 'geminiStoryboardGeneration',
  FreelancerAccountType = 'freelancerAccountType',
}

/* Default values should be:
  - Disabled for feature flags
  - Enabled for kill-switches
  - Reasonably safe and tested values for others
  - If unknown or the value is too long to keep in this file,
    you can provide a dummy value with `as` assertion
*/
export const defaultFeatureFlags = {
  [FeatureFlagName.OpenaiDefaultModel]: 'gpt-4o',
  [FeatureFlagName.CloneProject]: false,
  [FeatureFlagName.AnimationStyleSelectionVideos]: {} as Partial<
    Record<AnimationStyleEnum, string>
  >,
  [FeatureFlagName.PostProductionMediaManagement]: false,
  [FeatureFlagName.RoleCastingTalentAgeFilter]: false,
  [FeatureFlagName.TalentBadMatchThreshold]: 0.5,
  [FeatureFlagName.TestProjectButtonColor]: 'secondary' as ButtonProps['color'],
  [FeatureFlagName.GoogleDocsCallSheetExperiment]: '', // URL of the Google Docs template or empty string if disabled
  [FeatureFlagName.CallSheetV2]: false,
  [FeatureFlagName.HackathonCallSheetPoc]: false,
  [FeatureFlagName.Tasks]: false,
  [FeatureFlagName.TimelineV2]: false,
  [FeatureFlagName.CreativeProposals]: false,
  [FeatureFlagName.AdminReports]: false,
  [FeatureFlagName.ScriptedCallSheet]: false,
  [FeatureFlagName.ProposalProcessDefaultImages]: {} as Partial<{
    header: string;
    preproduction: string[];
    postproduction: string[];
  }>,
  [FeatureFlagName.ProposalDefaultImages]: {} as Partial<{
    header: string;
    footer: string;
  }>,
  [FeatureFlagName.CreativeProposalBudget]: false,
  [FeatureFlagName.GenerateContractFromSources]: false,
  [FeatureFlagName.StandaloneScriptPage]: false,
  [FeatureFlagName.GeminiStoryboardGeneration]: false,
  [FeatureFlagName.FreelancerAccountType]: false,
} satisfies Record<FeatureFlagName, unknown>;

export type FeatureFlags = typeof defaultFeatureFlags;
